import { useEffect, useMemo, useState } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import styled from 'styled-components'

const RELOAD_STORAGE_KEY_PREFIX = 'shiboruu:route-error-reload:'

const getErrorMessage = (error: unknown): string => {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`.trim()
  }

  if (error instanceof Error) {
    return error.message
  }

  return String(error)
}

const isRecoverableAssetLoadError = (message: string): boolean => {
  const normalizedMessage = message.toLowerCase()

  return (
    normalizedMessage.includes('dynamically imported module') ||
    normalizedMessage.includes('failed to fetch') ||
    normalizedMessage.includes('mime type') ||
    normalizedMessage.includes('text/html')
  )
}

export const RouteErrorElement = () => {
  const error = useRouteError()
  const message = useMemo(() => getErrorMessage(error), [error])
  const isRecoverable = isRecoverableAssetLoadError(message)
  const [hasAlreadyReloaded, setHasAlreadyReloaded] = useState(false)

  useEffect(() => {
    if (!isRecoverable) return

    const reloadStorageKey = `${RELOAD_STORAGE_KEY_PREFIX}${window.location.href}`
    if (sessionStorage.getItem(reloadStorageKey) === '1') {
      setHasAlreadyReloaded(true)
      return
    }

    sessionStorage.setItem(reloadStorageKey, '1')
    window.location.reload()
  }, [isRecoverable])

  const handleReload = () => {
    sessionStorage.removeItem(`${RELOAD_STORAGE_KEY_PREFIX}${window.location.href}`)
    window.location.reload()
  }

  return (
    <Screen>
      <Content>
        <div>
          <Title>{isRecoverable ? 'アプリを更新してください' : 'エラーが発生しました'}</Title>
          <Description>
            {isRecoverable
              ? hasAlreadyReloaded
                ? '更新後のファイルを読み込めませんでした。再読み込みすると復旧する場合があります。'
                : '更新後のファイルを読み込んでいます。'
              : '時間を置いて再度お試しください。'}
          </Description>
        </div>
        <ReloadButton type="button" onClick={handleReload}>
          再読み込み
        </ReloadButton>
      </Content>
    </Screen>
  )
}

const Screen = styled.main`
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: #f7f4ef;
  color: #1d1f22;
`

const Content = styled.section`
  width: min(100%, 420px);
  display: grid;
  gap: 16px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  line-height: 1.35;
`

const Description = styled.p`
  margin: 12px 0 0;
  font-size: 15px;
  line-height: 1.8;
`

const ReloadButton = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 8px;
  background: #1d1f22;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
`
